import React from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from './DateRangePicker';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebounce } from '@/hooks';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  title?: string;
  totalCount: number;
  enableSearch?: boolean;
  enableAddNewBtn?: boolean;
  enableUserRegisterTypeFilter?: boolean;
  enableContentTierFilter?: boolean;
  search?: string;
  searchPlaceholder?: string;
  contentTier?: string;
  onSearchChange?: (value: string) => void;
  onUserRegisterTypeChange?: (value: string) => void;
  onContentTierChange?: (value: string) => void;
  dateRange?: any;
  onDateRangeChange?: (range: any) => void;
  onAddClick?: () => void;
  addButtonText?: string;
  page: number;
  take: number;
  onPageChange: (page: number) => void;
  onTakeChange: (take: number) => void;
  isServerPaginated?: boolean;
  extraActions?: React.ReactNode;
  [key: string]: any;
}

interface SearchInputProps {
  value: string;
  placeholder: string;
  onChange?: (value: string) => void;
}

const SearchInput = React.memo(({ value, placeholder, onChange }: SearchInputProps) => {
  const [localValue, setLocalValue] = React.useState(value);
  const debouncedValue = useDebounce(localValue, 300);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  React.useEffect(() => {
    if (debouncedValue !== value) {
      onChange?.(debouncedValue);
    }
  }, [debouncedValue, onChange, value]);

  return (
    <div className="relative  max-w-xs">
      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className="w-full pl-8 bg-white border-input text-black max-w-[200px] "
      />
    </div>
  );
});

const UserRegisterTypeFilter = React.memo(
  ({ value, onChange }: { value: string; onChange?: (value: string) => void }) => {
    const selectValue = value || 'all';
    const handleValueChange = (val: string) => {
      onChange?.(val === 'all' ? '' : val);
    };

    return (
      <Select value={selectValue} onValueChange={handleValueChange}>
        <SelectTrigger className="w-[120px] bg-white border-input text-black h-10 rounded-md">
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All </SelectItem>
          <SelectItem value="Normal">Free</SelectItem>
          <SelectItem value="QR_Unlock">Premium</SelectItem>
        </SelectContent>
      </Select>
    );
  }
);

const ContentTierFilter = React.memo(
  ({ value, onChange }: { value: string; onChange?: (value: string) => void }) => {
    const selectValue = value || 'all';
    const handleValueChange = (val: string) => {
      onChange?.(val === 'all' ? '' : val);
    };

    return (
      <Select value={selectValue} onValueChange={handleValueChange}>
        <SelectTrigger className="w-[200px] bg-white border-input text-black h-10 rounded-md">
          <SelectValue placeholder="All Content" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Content</SelectItem>
          <SelectItem value="free">Free Content</SelectItem>
          <SelectItem value="premium">Premium Content</SelectItem>
        </SelectContent>
      </Select>
    );
  }
);

SearchInput.displayName = 'SearchInput';

function DataTableComponent<TData>({
  data,
  columns,
  title = 'Content Library',
  totalCount,
  onAddClick,
  addButtonText = 'Add new Content',
  take,
  page,
  dateRange,
  onDateRangeChange,
  onPageChange,
  onTakeChange,
  isServerPaginated: isServerPaginatedProp,
  search = '',
  userRegisterType = '',
  searchPlaceholder = 'Search by name,',
  enableSearch = true,
  enableAddNewBtn,
  enableUserRegisterTypeFilter,
  enableContentTierFilter,
  contentTier = '',
  onSearchChange,
  onUserRegisterTypeChange,
  onContentTierChange,
  extraActions,
}: DataTableProps<TData>) {
  const isServerPaginated = isServerPaginatedProp ?? totalCount > data.length;
  const paginatedData = isServerPaginated ? data : data.slice((page - 1) * take, page * take);

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    autoResetPageIndex: false,
  });

  const activePage = Number(page);
  const totalPages = Math.max(1, Math.ceil(totalCount / take));
  const currentPage = Math.min(Math.max(activePage, 1), totalPages);
  const hasResults = totalCount > 0;
  const start = hasResults ? (currentPage - 1) * take + 1 : 0;
  const end = hasResults ? Math.min(start + paginatedData.length - 1, totalCount) : 0;
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const showLeftEllipsis = activePage > 5;
    const showRightEllipsis = activePage < totalPages - 4;

    if (showLeftEllipsis && showRightEllipsis) {
      pages.push(1);
      pages.push('...');
      for (let i = activePage - 2; i <= activePage + 2; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (showLeftEllipsis) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 7; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      for (let i = 1; i <= 8; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };
  return (
    <div className=" bg-white rounded-[20px] shadow-sm border border-[#E9E9E9] overflow-hidden font-sans">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-4 py-3 sm:px-8 sm:py-6 ">
        <div className="flex  flex-col sm:flex-row items-start lg:items-center justify-between gap-4">
          <h2 className=" text-[16px] sm:text-[18px] font-bold text-[#1A1A1A] whitespace-nowrap">
            {title}
          </h2>
          {enableSearch && (
            <SearchInput value={search} placeholder={searchPlaceholder} onChange={onSearchChange} />
          )}

          {enableUserRegisterTypeFilter && (
            <UserRegisterTypeFilter value={userRegisterType} onChange={onUserRegisterTypeChange} />
          )}

          {enableContentTierFilter && (
            <ContentTierFilter value={contentTier} onChange={onContentTierChange} />
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {onDateRangeChange && (
            <DateRangePicker
              value={dateRange}
              onChange={(range) => onDateRangeChange(range)}
              placeholder="Filter by Date"
              className="w-[240px] border-input"
            />
          )}

          {extraActions}
          {enableAddNewBtn && (
            <Button
              type="button"
              onClick={onAddClick}
              className="w-full sm:w-auto bg-[#1E0036] hover:bg-lightPurple  text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2 max-w-[240px]"
            >
              <Plus className="h-4 w-4" /> {addButtonText}
            </Button>
          )}
        </div>
      </div>

      <div className="w-full overflow-x-auto relative">
        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-[#FAF4FF] border-y border-[#F0EEF9]">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="h-[52px] px-4 text-left  font-noto text-sm leading-none text-[#62556B] tracking-widest"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-[#F0EEF9] px-4">
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-[#FAF4FF]/30 transition-colors group ">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-2 sm:px-6 sm:py-5 align-middle text-sm text-[#1E0036] max-w-[240px] break-words sm:min-w-0 "
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-10 text-[#8E87A4] text-[16px]"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-3 sm:px-8 py-2 sm:py-6 border-t border-[#F0EEF9] flex flex-col lg:flex-row items-center justify-between bg-white ">
        <div className="flex items-center gap-2 max-w-full overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <Button
            type="button"
            variant="ghost"
            className="text-[#6B6B6B] font-bold hover:bg-transparent px-0 shrink-0"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          {getPageNumbers().map((item, index) =>
            item === '...' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-400 shrink-0">
                ...
              </span>
            ) : (
              <Button
                key={`page-${item}`}
                type="button"
                variant="ghost"
                onClick={() => onPageChange(Number(item))}
                className={`w-9 h-9 p-0 rounded-md shrink-0 ${
                  activePage === item
                    ? 'bg-[#1E0036] text-white'
                    : 'text-[#6B6B6B] hover:bg-gray-100'
                }`}
              >
                {item}
              </Button>
            )
          )}
          <Button
            type="button"
            variant="ghost"
            className="text-[#6B6B6B] font-bold hover:bg-transparent px-0 shrink-0"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div className="flex items-center gap-4 text-[#6B6B6B] text-sm ">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span>Result per page</span>
            <select
              value={take}
              onChange={(e) => onTakeChange(Number(e.target.value))}
              className="font-bold text-[#1A1A1A] bg-transparent outline-none"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="font-medium text-[#8E87A4] text-sm">
            <span className="text-[#1A1A1A] font-bold mr-1">
              {start}-{end}
            </span>
            of {totalCount}
          </div>
        </div>
      </div>
    </div>
  );
}

export const DataTable = React.memo(DataTableComponent) as typeof DataTableComponent;
